import { create } from "zustand";
import { defaultDateFrom } from "../lib/defaultDateRange";

export type Period = "monthly" | "quarterly";
export type ConsolidatedMode = "single" | "consolidated";
export type RemontierungMode = "rolling" | "month";
export type BenchmarkMode = "all" | "size_class" | "peer_group" | "milking_system";
export type BenchmarkSizeClass = "0-400" | "400-600" | "600-1000" | ">1000";
export type BenchmarkMilkingSystem = "2x" | "3x" | "robot";
export type BenchmarkSelection =
  | { mode: "all"; value: null }
  | { mode: "size_class"; value: BenchmarkSizeClass }
  | { mode: "peer_group"; value: number }
  | { mode: "milking_system"; value: BenchmarkMilkingSystem };
export type BenchmarkSurfaceSelection =
  | { mode: "all"; value: null }
  | { mode: "size_class"; value: BenchmarkSizeClass }
  | { mode: "peer_group"; value: number };

interface AppState {
  selectedHerdId: string | null;
  period: Period;
  dateFrom: string | null;
  dateTo: string | null;
  /** ISO "YYYY-MM" months derived from loaded timeseries data, sorted asc. */
  availableMonths: string[];
  /** Label of the last Analyse section whose KPI tile was clicked (for feedback context). */
  lastAnalyseSection: string | null;
  /** Ephemeral, chart-scoped benchmark choices. Deliberately not persisted. */
  benchmarkSelections: Record<string, BenchmarkSelection>;
  /** null means use the selected herd's automatic size-class default. */
  benchmarkSurface: BenchmarkSurfaceSelection | null;
  /** Multi-Standort: 'single' = beide Profile einzeln, 'consolidated' = konsolidiert */
  consolidatedMode: ConsolidatedMode;
  remontierungMode: RemontierungMode;
  remontierungSnapshotId: string | null;
  setHerd: (herdId: string | null) => void;
  setPeriod: (period: Period) => void;
  setDateFrom: (d: string | null) => void;
  setDateTo: (d: string | null) => void;
  setDateRange: (from: string | null, to: string | null) => void;
  setPeriodAndDateRange: (
    period: Period,
    from: string | null,
    to: string | null,
  ) => void;
  setAvailableMonths: (months: string[]) => void;
  setLastAnalyseSection: (label: string | null) => void;
  setBenchmarkSelection: (stateKey: string, selection: BenchmarkSelection) => void;
  setBenchmarkSelections: (selections: Record<string, BenchmarkSelection>) => void;
  setBenchmarkSurface: (selection: BenchmarkSurfaceSelection | null) => void;
  setConsolidatedMode: (mode: ConsolidatedMode) => void;
  setRemontierungMode: (mode: RemontierungMode) => void;
  setRemontierungSnapshotId: (snapshotId: string | null) => void;
}

function readUrlParam(key: string): string | null {
  try {
    return new URLSearchParams(window.location.search).get(key);
  } catch {
    return null;
  }
}

function pushUrlParam(key: string, value: string | null) {
  try {
    const url = new URL(window.location.href);
    if (value == null) {
      url.searchParams.delete(key);
    } else {
      url.searchParams.set(key, value);
    }
    window.history.replaceState(null, "", url.toString());
  } catch {
    /* noop */
  }
}

/** Default "to" month: current month. */
function defaultDateTo(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** Return value if it looks like "YYYY-MM", else null. */
function validIsoMonth(s: string | null): string | null {
  if (!s) return null;
  return /^\d{4}-\d{2}$/.test(s) ? s : null;
}

const initialHerd = readUrlParam("herd_id");
const initialPeriodRaw = readUrlParam("period");
const initialPeriod: Period =
  initialPeriodRaw === "monthly" ? "monthly" : "quarterly";

const initialDateFrom =
  validIsoMonth(readUrlParam("from_month")) ?? defaultDateFrom(initialPeriod);
const initialDateTo =
  validIsoMonth(readUrlParam("to_month")) ?? defaultDateTo();

export const useAppStore = create<AppState>((set) => ({
  selectedHerdId: initialHerd,
  period: initialPeriod,
  dateFrom: initialDateFrom,
  dateTo: initialDateTo,
  availableMonths: [],
  lastAnalyseSection: null,
  benchmarkSelections: {},
  benchmarkSurface: null,
  consolidatedMode: readUrlParam("scope") === "consolidated" ? "consolidated" : "single",
  remontierungMode: readUrlParam("remon_mode") === "month" ? "month" : "rolling",
  remontierungSnapshotId: null,

  setHerd: (herdId) => {
    pushUrlParam("herd_id", herdId);
    set((state) => state.selectedHerdId === herdId
      ? { selectedHerdId: herdId }
      : {
          selectedHerdId: herdId,
          benchmarkSelections: {},
          benchmarkSurface: null,
        });
  },

  setPeriod: (period) => {
    pushUrlParam("period", period);
    set({ period });
  },

  setDateFrom: (d) => {
    pushUrlParam("from_month", d);
    set({ dateFrom: d });
  },

  setDateTo: (d) => {
    pushUrlParam("to_month", d);
    set({ dateTo: d });
  },

  setDateRange: (from, to) => {
    pushUrlParam("from_month", from);
    pushUrlParam("to_month", to);
    set({ dateFrom: from, dateTo: to });
  },

  setPeriodAndDateRange: (period, from, to) => {
    pushUrlParam("period", period);
    pushUrlParam("from_month", from);
    pushUrlParam("to_month", to);
    set({ period, dateFrom: from, dateTo: to });
  },

  setAvailableMonths: (months) => {
    set({ availableMonths: months });
  },

  setLastAnalyseSection: (label) => {
    set({ lastAnalyseSection: label });
  },

  setBenchmarkSelection: (stateKey, selection) => {
    set((state) => ({
      benchmarkSelections: { ...state.benchmarkSelections, [stateKey]: selection },
    }));
  },
  setBenchmarkSelections: (benchmarkSelections) => set({ benchmarkSelections }),
  setBenchmarkSurface: (selection) => set({ benchmarkSurface: selection }),

  setConsolidatedMode: (mode) => {
    pushUrlParam("scope", mode);
    set({ consolidatedMode: mode });
  },
  setRemontierungMode: (mode) => {
    pushUrlParam("remon_mode", mode);
    set({ remontierungMode: mode, remontierungSnapshotId: null });
  },
  setRemontierungSnapshotId: (remontierungSnapshotId) => set({ remontierungSnapshotId }),
}));
